import { useEffect, useState } from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';
import { PageUrls, getBestPricePagePath } from '../../App';
import { useSession } from '../../hooks/useSession';
import { IBook, IUserAccountRegion, UnsupportedReason } from '../../types';
import SecurityMessagePage from './security-message';
import SelectRoot from './select-root';
import Success from './success';
import UserLogin from './user-login';

export default function LinkRootBook() {
    const session = useSession();
    const { push } = useHistory();

    // Extension version
    const [extensionVersion, setExtensionVersion] = useState<string | null>(
        null
    );
    const [versionChecked, setVersionChecked] = useState(false);

    useEffect(() => {
        setExtensionVersion(
            sessionStorage.getItem('sharpSportsExtensionVersion')
        );
        setVersionChecked(true);
    }, []);

	const redirectOrExit = () => {
        if (session?.isBestPrice){
            push(getBestPricePagePath(session?.cid || ''));
        }
		else if (session?.redirectUrl){
			window.location.href = session?.redirectUrl;
		} else {
			window.close();
			window.location.href = '/done';
		}
	}
    const checkVersionSupport = (
        abbr: string,
        service: string | null,
        minSdkVersions: string[]
    ) => {
        if (!service) return false;

        let s = service.split('_');
        let version = s[1];
        //Compare numerically: as strings "10" sorts below "9", so SDK 2.10.0
        //would be rejected by a minimum of 2.9.0
        let [major, minor, patch] = version.split('.').map(Number);
        if (isNaN(major) || isNaN(minor) || isNaN(patch)) return false;

        if (abbr == 'dk' && s[0] == 'sharpsports-mobile-link')
            return version >= '2.4.0';

        for (let minV of minSdkVersions) {
            let [minMajor, minMinor, minPatch] = minV.split('.').map(Number);
            //find the right major version
            if (minMajor !== major) continue;
            if (minMinor < minor) return true;
            if (minMinor > minor) return false;
            //if minor version is equal to minimum, check patch
            if (minPatch <= patch) return true;
        }
        return false;
    };

    const getUnsupportedReason = (book: IBook | undefined, selectedUserAccountRegion: IUserAccountRegion | undefined) => {

        //If userAccount/userAccount region has active=false then surface error
        if (!selectedUserAccountRegion?.active){
            return UnsupportedReason.UserAccountInactive
        }

        let status = selectedUserAccountRegion?.bookRegion?.status || ''
        let userAgent = session?.testUserAgent
            ? session?.testUserAgent
            : navigator.userAgent;

        //book is down for maintanence or coming soon
        if (status === 'inactive') {
            return UnsupportedReason.Maintainence;
        } else if (status === 'coming') {
            return UnsupportedReason.ComingSoon;
        }

        //if book id not in sdkSupport dict then this book does not have any sdk requirements
        if (!book || !session?.sdkSupport[book.id])
            return UnsupportedReason.isSupported;

        //If extension context return no web for books that are native only but not yet supported on Browsr extension
        let bookSupport = session?.sdkSupport[book.id];
        if (session?.extensionAuthToken && bookSupport) {
            let supportedExtensions =
                bookSupport?.webBrowserExtension.platforms.filter((x: any) => {
                    return x.supported === true;
                });
            if (supportedExtensions.length == 0) {
                return UnsupportedReason.noWeb;
            }
            //If book supported on extension then prompt to download extension if not already
            else {
                return extensionVersion
                    ? UnsupportedReason.isSupported
                    : UnsupportedReason.DownloadExtension;
            }
        }

        //Case of mobile only book/region when auth and service query params are not both present
        if (book?.sdkRequired && !(session?.auth && session?.service)) {
            return UnsupportedReason.MobileOnly;
        }

        //assign the sdk from the service
        //console.log("SDK SUPPORT: ", session.sdkSupport[book.id])
        //console.log("SERVICE: ", session?.service)
        let bookSdkSupport;
        if (session?.service?.includes('android')) {
            bookSdkSupport = session.sdkSupport[book.id].androidNative;
        } else if (session?.service?.includes('cordova')) {
            bookSdkSupport = session.sdkSupport[book.id].cordova;
        } else if (session?.service?.includes('spm')) {
            bookSdkSupport = session.sdkSupport[book.id].iOSNative;
        } else bookSdkSupport = session.sdkSupport[book.id].reactNative; //else to include deprecated sharpsports d-mobile-link RN

        //console.log("UserAgent: ", userAgent)
        //console.log("bookSdkSupport: ", bookSdkSupport)
        //need to validate for iOS
        let platform = /Android/i.test(userAgent) ? 'android' : 'iOS';
        let platformSupport = bookSdkSupport.platforms.find(
            (p: any) => p.name === platform
        );

        //check if there is any support for this platform + service
        if (
            platformSupport === undefined ||
            platformSupport.supported === false
        ) {
            return platform === 'android'
                ? UnsupportedReason.Android
                : UnsupportedReason.iOS;
        }

        //if supported = true, this must be an array of length 1+
        if (
            !checkVersionSupport(
                book.abbr,
                session?.service || null,
                platformSupport.minSdkVersions
            )
        ) {
            return UnsupportedReason.MobileVersion;
        }

        //all other books are supported on all platforms
        return UnsupportedReason.isSupported;
    };

    return (
        <Switch>
            <Route
                path={[PageUrls.LOCATION_SELECT, PageUrls.BOOK_LIST]}
                render={() => (
                    <SelectRoot getUnsupportedReason={getUnsupportedReason} />
                )}
            />
            <Route
                path={PageUrls.LOGIN_FORM}
                render={() => (
                    <UserLogin
                        getUnsupportedReason={getUnsupportedReason}
                        extensionVersion={extensionVersion}
                        versionChecked={versionChecked}
                    />
                )}
            />
            <Route
                path={PageUrls.LINK_SUCCESS}
                render={() => <Success redirectOrExit={redirectOrExit} />}
            />
            <Route
                path={PageUrls.SECURITY_MESSAGE}
                component={SecurityMessagePage}
            />
        </Switch>
    );
}
